import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { VexPopoverRef } from '@vex/components/vex-popover/vex-popover-ref';
import { MatRippleModule } from '@angular/material/core';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '@core/services/auth/auth.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { CommonModule } from '@angular/common';
import { WorkspaceStore } from 'src/app/shared/services/workspace.store';
import { WorkspaceMembershipModel } from 'src/app/shared/definitions/workspace-membership';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'vex-sidenav-user-menu',
  templateUrl: './sidenav-user-menu.component.html',
  styleUrls: ['./sidenav-user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatRippleModule,
    RouterLink,
    MatIconModule,
    CommonModule,
    TranslatePipe
  ]
})
export class SidenavUserMenuComponent {
  private readonly popoverRef = inject(VexPopoverRef);
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);

  readonly user = this.authStore.user;
  readonly currentWorkspaceId = this.workspaceStore.currentWorkspaceId;
  readonly workspaces = signal<WorkspaceMembershipModel[]>([]);

  readonly isSuperAdmin = computed(
    () => this.user()?.hasRole('superadmin') ?? false
  );

  constructor() {
    // Re-hydrate membership list whenever the underlying workspace list changes.
    // Memberships require async enrichment so we can't derive them via computed().
    effect(() => {
      const _trigger = this.workspaceStore.userWorkspaces();
      void this.reloadMemberships();
    });
  }

  private async reloadMemberships(): Promise<void> {
    const memberships = await this.workspaceStore.getUserWorkspacesMemberships();
    this.workspaces.set(memberships);
  }

  close(): void {
    /** Wait for animation to complete and then close */
    setTimeout(() => this.popoverRef.close(), 250);
  }

  async logout(): Promise<void> {
    try {
      await this.authService.logout();
      this.close();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  getCurrentWorkspaceName(): string {
    const id = this.currentWorkspaceId();
    if (!id) return 'No Workspace';
    const workspace = this.workspaces().find(w => w.workspaceId === id);
    return workspace?.workspaceName || 'Unknown Workspace';
  }

  getWorkspaceColor(workspace: WorkspaceMembershipModel | undefined): string {
    if (workspace?.workspaceColor) {
      return workspace.workspaceColor;
    }

    if (workspace?.workspaceName) {
      const colors = [
        '#3b82f6',
        '#8b5cf6',
        '#ec4899',
        '#f59e0b',
        '#10b981',
        '#06b6d4',
        '#6366f1',
        '#ef4444'
      ];

      const hash = workspace.workspaceName
        .split('')
        .reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);

      return colors[hash % colors.length];
    }

    return '#3b82f6';
  }

  getCurrentWorkspace(): WorkspaceMembershipModel | undefined {
    const id = this.currentWorkspaceId();
    return this.workspaces().find(w => w.workspaceId === id);
  }

  canManageWorkspace(): boolean {
    const workspace = this.getCurrentWorkspace();
    return workspace?.role === 'owner' || workspace?.role === 'admin';
  }

  getWorkspaceInitials(workspace: WorkspaceMembershipModel | undefined): string {
    if (!workspace?.workspaceName) return '?';

    const words = workspace.workspaceName.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  }
}
